import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, ArrowUp } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import Logo from "@/components/Logo";
import {
  SECTION_IDS,
  useLang,
  useSectionNav,
  scrollPageTop,
} from "@/lib/navigation";
import { openCookiePreferences } from "@/components/CookieConsent";

const Footer = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const goToSection = useSectionNav();
  const year = new Date().getFullYear();

  const sectionLinks = [
    { id: SECTION_IDS.why, label: t("nav.why"), testId: "footer-link-why" },
    { id: SECTION_IDS.circuit, label: t("nav.circuit"), testId: "footer-link-circuit" },
    {
      id: SECTION_IDS.transparency,
      label: t("nav.transparency"),
      testId: "footer-link-transparency",
    },
    { id: SECTION_IDS.imagery, label: t("nav.imagery"), testId: "footer-link-imagery" },
    { id: SECTION_IDS.contact, label: t("nav.contact"), testId: "footer-link-contact" },
  ];

  const legalRows = [
    t("footer.legal.company"),
    t("footer.legal.cuit"),
    t("footer.legal.vat"),
    t("footer.legal.registration"),
    t("footer.legal.license"),
    t("footer.legal.data_registry"),
    t("footer.legal.incorporated"),
    t("footer.legal.jurisdiction"),
  ];

  return (
    <footer
      className="border-t border-border bg-[hsl(var(--surface-2))]"
      data-testid="site-footer"
    >
      <div className="strata-band h-1.5 w-full" aria-hidden="true" />
      <div className="container-page py-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <Link
              to={`/${lang}`}
              className="inline-flex"
              aria-label={t("brand.name")}
              data-testid="footer-logo-link"
            >
              <Logo title={t("brand.tagline")} />
            </Link>
            <h3 className="mt-6 text-sm font-semibold text-foreground">
              {t("footer.about_title")}
            </h3>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {t("footer.about_text")}
            </p>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-data text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
              {t("footer.nav_title")}
            </h3>
            <ul className="mt-4 space-y-2.5">
              {sectionLinks.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    onClick={() => goToSection(link.id)}
                    className="text-left text-sm text-[hsl(var(--ink-2))] transition-colors hover:text-[hsl(var(--terracotta))]"
                    data-testid={link.testId}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-data text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
              {t("footer.legal_nav_title")}
            </h3>
            <ul className="mt-4 space-y-2.5">
              <li>
                <Link
                  to={`/${lang}/privacy-policy`}
                  className="text-sm text-[hsl(var(--ink-2))] transition-colors hover:text-[hsl(var(--terracotta))]"
                  data-testid="footer-link-privacy"
                >
                  {t("nav.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${lang}/cookie-policy`}
                  className="text-sm text-[hsl(var(--ink-2))] transition-colors hover:text-[hsl(var(--terracotta))]"
                  data-testid="footer-link-cookies"
                >
                  {t("nav.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${lang}/terms-conditions`}
                  className="text-sm text-[hsl(var(--ink-2))] transition-colors hover:text-[hsl(var(--terracotta))]"
                  data-testid="footer-link-terms"
                >
                  {t("nav.terms")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookiePreferences}
                  className="text-left text-sm text-[hsl(var(--ink-2))] transition-colors hover:text-[hsl(var(--terracotta))]"
                  data-testid="footer-cookie-settings-button"
                >
                  {t("footer.cookie_settings")}
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-data text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
              {t("footer.contact_title")}
            </h3>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-2.5 text-sm">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--terracotta))]" />
                <a
                  href={`mailto:${t("footer.email")}`}
                  className="text-[hsl(var(--ink-2))] underline-offset-4 transition-colors hover:text-[hsl(var(--terracotta))] hover:underline"
                  data-testid="footer-email-link"
                >
                  {t("footer.email")}
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-sm">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--terracotta))]" />
                <a
                  href={`tel:${t("footer.phone").replace(/\s/g, "")}`}
                  className="text-[hsl(var(--ink-2))] underline-offset-4 transition-colors hover:text-[hsl(var(--terracotta))] hover:underline"
                  data-testid="footer-phone-link"
                >
                  {t("footer.phone")}
                </a>
              </li>
            </ul>
            <h4 className="mt-6 font-data text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
              {t("footer.addresses_title")}
            </h4>
            <ul className="mt-3 space-y-2.5 text-sm text-muted-foreground">
              {[
                t("footer.address_legal"),
                t("footer.address_salta"),
                t("footer.address_neuquen"),
              ].map((addr) => (
                <li key={addr} className="flex items-start gap-2.5">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--mineral-gray))]" />
                  <span>{addr}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-10" />

        <div>
          <h3 className="font-data text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
            {t("footer.legal_title")}
          </h3>
          <ul
            className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-xs leading-relaxed text-muted-foreground sm:grid-cols-2"
            data-testid="footer-legal-details"
          >
            {legalRows.map((row) => (
              <li key={row}>{row}</li>
            ))}
          </ul>
          <p className="mt-6 max-w-4xl text-xs leading-relaxed text-muted-foreground">
            {t("footer.disclaimer")}
          </p>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-border pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-muted-foreground">
            © {year} {t("footer.rights")}
          </p>
          <button
            type="button"
            onClick={scrollPageTop}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-xs font-medium text-[hsl(var(--ink-2))] transition-colors hover:bg-secondary"
            data-testid="footer-back-to-top-button"
          >
            <ArrowUp className="h-3.5 w-3.5" />
            {t("common.back_to_top")}
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
